import Head from 'next/head'
import Link from 'next/link'
import React, { useRef, RefObject, useState, useEffect, useMemo, useCallback, FC, Fragment } from 'react'
import { Carousel, Typography, Collapse, Divider, message, Button, Tabs } from 'antd'
import { CarouselRef } from 'antd/es/carousel'
import { DEFAULT_IMG_SOURCE } from '../MaterialItem'
import { AuthApi, IDatahubCase, ISimplifiedVisualMaterial } from '@libs/api'
import { ArrowLeft24, ArrowRight24, Link24, ChevronUp24, ChevronDown24 } from '@carbon/icons-react'
import { Container, Grid, IconButton } from '@libs/components'
// import ShareLink from 'react-facebook-share-link'
import Styled from './Styled'
import { useLocalization } from '@libs/localization'
import { isVideoFormat } from '@libs/util'
import { useScreenMatch } from '@libs/theme'
import appRoutes from 'app.routes'
import { useCaseById } from 'modules/cases/hooks'
import { useWhitelabelSettings } from 'modules/branches/hooks'

export interface PresentationPageContentProps {
	caseId: IDatahubCase['id']
	filter: string
	visualMaterials: ISimplifiedVisualMaterial[]
	notFound?: boolean
}

const PresentationPageContent: FC<PresentationPageContentProps> = ({ visualMaterials, caseId, filter }) => {
	const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
	const [openedPanelKeys, setOpenedPanelKeys] = useState<string[]>([])
	const navContainerRef = useRef<HTMLDivElement>() as RefObject<HTMLDivElement>
	const carouselRef = useRef<CarouselRef>() as RefObject<CarouselRef>
	const match = useScreenMatch()
	const { data: caseDetails } = useCaseById(caseId)
	const materialsByType = useMemo<Record<string, ISimplifiedVisualMaterial[]>>(
		() => ({
			...visualMaterials.reduce((memo, next) => {
				if (!memo[next.type]) {
					memo[next.type] = [next]
				} else {
					memo[next.type].push(next)
				}
				return memo
			}, {} as Record<string, ISimplifiedVisualMaterial[]>),
			all: visualMaterials,
		}),
		[visualMaterials]
	)
	const TAB_KEYS = useMemo(() => Object.keys(materialsByType).filter(key => key !== 'all'), [materialsByType])
	const { data: whitelabel, isLoading: areWhitelabelLoading } = useWhitelabelSettings(AuthApi.isAuthenticated())

	const [currentTabKey, setCurrentTabKey] = useState('all')
	const filteredMaterials = useMemo(() => materialsByType[currentTabKey] || [], [materialsByType, currentTabKey])

	useEffect(() => {
		const thumbnailWidth = (navContainerRef.current?.clientWidth || 0) / 12

		navContainerRef.current?.scrollTo({
			left: thumbnailWidth * currentSlideIndex,
			behavior: 'smooth',
		})
	}, [currentSlideIndex])

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			switch (e.key) {
				case 'ArrowLeft':
					onPrevClick()
					break
				case 'ArrowRight':
					onNextClick()
					break
			}
		}

		document.addEventListener('keydown', onKeyDown)

		return () => {
			document.removeEventListener('keydown', onKeyDown)
		}
	}, [])

	const onPrevClick = () => {
		carouselRef.current?.prev()
	}

	const onNextClick = () => {
		carouselRef.current?.next()
	}

	const { t } = useLocalization()

	const copyLink = useCallback(
		async e => {
			e.stopPropagation()
			try {
				navigator.clipboard.writeText(window.location.href)
				message.success(t('materials.pages.presentation.linkCopyToClipboardSuccess'))
			} catch (ex) {
				message.error((ex as any).message)
			}
		},
		[t]
	)

	const panelHeader = useMemo(
		() => (
			<Styled.PanelHeader>
				<Grid.Col>
					<Container display="flex" space="sm">
						<Container display="flex" alignItems="center">
							{openedPanelKeys.includes('main') ? <ChevronDown24 /> : <ChevronUp24 />}
						</Container>
						<Tabs size="small" activeKey={currentTabKey} onChange={setCurrentTabKey}>
							<Tabs.TabPane
								key="all"
								tab={filter === 'all' ? t('common.terms.allAssets') : t('common.terms.activeAssets')}
								tabKey="all"
							/>
							{TAB_KEYS.map(key => (
								<Tabs.TabPane key={key} tab={t(`materials.types.${key}`)} tabKey={key} />
							))}
						</Tabs>
					</Container>
				</Grid.Col>
				<Grid.Col>
					<Divider type="vertical" />
				</Grid.Col>
				<Grid.Col>
					<IconButton title={t('common.actions.copyToClipboard')} onClick={copyLink}>
						<Link24 />
					</IconButton>
				</Grid.Col>
				{/* <Grid.Col>
					<ShareLink>
						{(link: string) => (
							<IconButton
								title={t('common.actions.facebookShare')}
								onClick={e => {
									e.stopPropagation()
									window.open(link)
								}}
							>
								<LogoFacebook24 />
							</IconButton>
						)}
					</ShareLink>
				</Grid.Col> */}
				{match.lte('lg') && (
					<Styled.MobileArrows display={'flex'} alignItems={'center'} space={'xxs'} spaceDirection={'horizontal'}>
						<Button
							type={'text'}
							size={'small'}
							onClick={e => {
								e.preventDefault()
								e.stopPropagation()
								onPrevClick()
							}}
						>
							{t('navigation.prev')}
						</Button>
						<Typography.Text>/</Typography.Text>
						<Button
							type={'text'}
							size={'small'}
							onClick={e => {
								e.preventDefault()
								e.stopPropagation()
								onNextClick()
							}}
						>
							{t('navigation.next')}
						</Button>
					</Styled.MobileArrows>
				)}
			</Styled.PanelHeader>
		),
		[filter, openedPanelKeys, copyLink, t, match, TAB_KEYS, currentTabKey]
	)

	const currentSlideName = filteredMaterials[currentSlideIndex]?.description

	if (areWhitelabelLoading && AuthApi.isAuthenticated()) return null

	return (
		<>
			<Head>
				<title>{t(`navigation.pageTitles.presentation`, { address: caseDetails?.address })}</title>
				<meta property="og:url" content="https://booqly.dk/presentation" />
				<meta property="og:type" content="Website" />
				<meta property="og:title" content={t('materials.pages.presentation.og.title')} />
				<meta property="og:description" content={t('materials.pages.presentation.og.subTitle', { caseId })} />
				<meta property="og:image" content={visualMaterials[0].source} />
				<meta property="og:locale" content="en_US" />
				<meta property="og:locale:alternate" content="da_DK" />
				<meta property="og:locale:alternate" content="sv_SE" />
			</Head>
			<Styled.Container>
				<Styled.CarouselWrapper>
					<Carousel
						dots={false}
						ref={carouselRef}
						lazyLoad="progressive"
						beforeChange={(_, nextSlideIndex) => setCurrentSlideIndex(nextSlideIndex)}
					>
						{filteredMaterials.map(({ source, description, type }, index) => {
							const shouldRenderInIframe = type === 'PanoramasHtml5' || source?.match(/.html$/)
							const isVideo = isVideoFormat(source)

							let component = <img src={source || DEFAULT_IMG_SOURCE} alt={description || 'material image'} />

							if (shouldRenderInIframe) {
								component = (
									<object
										width="100%"
										data={source}
										style={{ visibility: currentSlideIndex === index ? 'visible' : 'hidden' }}
									/>
								)
							}

							if (isVideo) {
								component = (
									<video width="100%" height="100%" controls>
										<source src={source} />
									</video>
								)
							}

							return (
								<Styled.SlideWrapper key={index} display="flex" justifyContent="center">
									{component}
								</Styled.SlideWrapper>
							)
						})}
					</Carousel>
				</Styled.CarouselWrapper>
				<Link href={appRoutes.externalLinks.esoft()} passHref>
					<a>
						<Styled.Logo src={whitelabel?.logoUrl || '/logo.png'} />
					</a>
				</Link>
				{match.gte('xl') && (
					<Fragment>
						<Styled.PrevButton type="primary" size="large" onClick={onPrevClick}>
							<ArrowLeft24 />
						</Styled.PrevButton>
						<Styled.NextButton type="primary" size="large" onClick={onNextClick}>
							<ArrowRight24 />
						</Styled.NextButton>
					</Fragment>
				)}
				{filteredMaterials.length > 0 && (
					<>
						<Styled.SummaryWrapper size="small">
							<Grid.Row>
								{currentSlideName && (
									<Grid.Col>
										<Typography.Text ellipsis style={{ maxWidth: 300 }}>
											{currentSlideName}
										</Typography.Text>
									</Grid.Col>
								)}
								<Grid.Col>
									<Typography.Text>
										{currentSlideIndex + 1} of {filteredMaterials.length}
									</Typography.Text>
								</Grid.Col>
							</Grid.Row>
						</Styled.SummaryWrapper>
						<Styled.NavigationContainer>
							<Collapse onChange={keys => setOpenedPanelKeys(keys as string[])} expandIcon={() => null}>
								<Collapse.Panel key="main" header={panelHeader}>
									<Grid.Row ref={navContainerRef} gutter={match.map({ xxs: 'xxs', md: 'sm' })}>
										{filteredMaterials.map(({ thumbnailUrl }, index) => (
											<Grid.Col key={index} xs={6} md={4} xl={2}>
												<Styled.ThumbnailWrapper
													active={index === currentSlideIndex}
													onClick={() => carouselRef.current?.goTo(index)}
												>
													<img src={thumbnailUrl} alt="Asset thumbnail" />
												</Styled.ThumbnailWrapper>
											</Grid.Col>
										))}
									</Grid.Row>
								</Collapse.Panel>
							</Collapse>
						</Styled.NavigationContainer>
					</>
				)}
			</Styled.Container>
		</>
	)
}

export default PresentationPageContent
